import { useState } from 'react'
import type { ForecastWeek } from '../../api/cashReport'
import type { AriaCashForecastWeek } from '../../api/ariaChat'
import {
  buildForecastBars,
  formatAxisMoney,
  weekCloseReadout,
  type ChartWeekBar,
} from '../../components/aria/cashChartModel'
import { cashReportChartDomain, niceAxisTicks } from './chartTicks.js'

type Props = {
  weeks: ForecastWeek[]
  cushionCents: number
  lowestWeek?: string | null
  onOpenWeek?: (weekStart: string) => void
}

const W = 400
const H = 168
const PAD = { top: 18, right: 10, bottom: 28, left: 78 }
const INNER_W = W - PAD.left - PAD.right
const INNER_H = H - PAD.top - PAD.bottom

function yScale(v: number, min: number, max: number): number {
  return PAD.top + INNER_H * (1 - (v - min) / (max - min))
}

function areaBelow(bars: ChartWeekBar[], threshold: number, min: number, max: number, slot: number) {
  if (bars.length < 2) return ''
  const pts: string[] = []
  bars.forEach((b, i) => {
    const x = PAD.left + slot * i + slot / 2
    const y = yScale(Math.min(b.closeAmount, threshold), min, max)
    pts.push(`${x},${y}`)
  })
  const lastX = PAD.left + slot * (bars.length - 1) + slot / 2
  const firstX = PAD.left + slot / 2
  const threshY = yScale(threshold, min, max)
  return `${firstX},${threshY} ${pts.join(' ')} ${lastX},${threshY}`
}

export default function CashReportChart({ weeks, cushionCents, lowestWeek, onOpenWeek }: Props) {
  const mapped: AriaCashForecastWeek[] = weeks.map((w) => ({
    week_start: w.week_start,
    in: w.in ? { amount: w.in.amount, formatted: w.in.formatted || '' } : undefined,
    close: w.close ? { amount: w.close.amount, formatted: w.close.formatted || '' } : undefined,
    subs_out: w.subs_out ? { amount: w.subs_out.amount, formatted: w.subs_out.formatted || '' } : undefined,
    payroll: w.payroll ? { amount: w.payroll.amount, formatted: w.payroll.formatted || '' } : undefined,
    overhead: w.overhead ? { amount: w.overhead.amount, formatted: w.overhead.formatted || '' } : undefined,
    other_out: w.other_out ? { amount: w.other_out.amount, formatted: w.other_out.formatted || '' } : undefined,
  }))
  const bars = buildForecastBars(mapped, {
    status: 'positive',
    lowest_week: lowestWeek || undefined,
    first_short_week: weeks.find((w) => w.below_zero || w.below_cushion)?.week_start,
  })
  const [active, setActive] = useState<number | null>(null)
  if (!bars.length) return null
  const cushion = cushionCents / 100
  const { min: domainMin, max: domainMax } = cashReportChartDomain(
    bars.map((b) => b.closeAmount),
    cushion,
  )
  const slot = INNER_W / bars.length
  const closePts = bars
    .map((b, i) => `${PAD.left + slot * i + slot / 2},${yScale(b.closeAmount, domainMin, domainMax)}`)
    .join(' ')
  const tip = active != null ? bars[active] : null

  function step(delta: number) {
    setActive((cur) => {
      const base = cur == null ? (delta > 0 ? -1 : bars.length) : cur
      return Math.max(0, Math.min(bars.length - 1, base + delta))
    })
  }

  return (
    <div
      data-testid="cr-chart"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); step(1) }
        if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1) }
        if ((e.key === 'Enter' || e.key === ' ') && tip) {
          e.preventDefault()
          onOpenWeek?.(tip.week_start)
        }
      }}
    >
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Eight-week company cash balance">
        <polygon
          points={areaBelow(bars, 0, domainMin, domainMax, slot)}
          fill="#fcecec"
          data-testid="cr-chart-fill-zero"
        />
        <polygon
          points={areaBelow(bars, cushion, domainMin, domainMax, slot)}
          fill="#eaf6ef"
          data-testid="cr-chart-fill-cushion"
        />
        {niceAxisTicks(domainMin, domainMax, 4).map((t) => (
          <text
            key={t}
            x={PAD.left - 6}
            y={yScale(t, domainMin, domainMax) + 3}
            textAnchor="end"
            className="aria-cash-chart-axis"
          >
            {formatAxisMoney(t)}
          </text>
        ))}
        <polyline points={closePts} fill="none" className="aria-cash-chart-close-line" />
        {bars.map((b, i) => {
          const cx = PAD.left + slot * i + slot / 2
          return (
            <g
              key={b.week_start}
              tabIndex={0}
              role="button"
              data-testid={`cr-chart-week-${i}`}
              aria-label={weekCloseReadout(b.week_start, b.closeAmount, b.closeFormatted)}
              onFocus={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              onClick={() => onOpenWeek?.(b.week_start)}
            >
              <rect x={PAD.left + slot * i} y={PAD.top} width={slot} height={INNER_H} fill="transparent" />
              <circle cx={cx} cy={yScale(b.closeAmount, domainMin, domainMax)} r={b.isLowest ? 3.5 : 2.5} />
              <text x={cx} y={H - 8} textAnchor="middle" className="aria-cash-chart-week-label">{b.weekLabel}</text>
            </g>
          )
        })}
      </svg>
      <div className="cr-chart-readout" data-testid="cr-chart-readout" role="status">
        {tip ? weekCloseReadout(tip.week_start, tip.closeAmount, tip.closeFormatted) : ''}
      </div>
    </div>
  )
}
